import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertCircle, Send, Edit, Printer, Share2, Download } from 'lucide-react';
import { useCompany } from '../context/CompanyContext';
import { formatCurrency, formatDate, cn } from '../utils/formatters';
import { buildBudgetShareText, absoluteAppUrl } from '../utils/share';
import { ShareSheet } from '../components/ui/ShareSheet';
import { CardSkeleton } from '../components/ui/LoadingScreen';

export function BudgetDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { budgets, clients, company, isLoading } = useCompany();
  const [budget, setBudget] = useState(() => budgets.find(b => b.id === id));
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    setBudget(budgets.find(b => b.id === id));
  }, [budgets, id]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Presupuesto</h1>
        <CardSkeleton />
      </div>
    );
  }

  if (!budget) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-danger-500" />
          <h1 className="text-2xl font-bold text-danger-600 mb-4">Presupuesto no encontrado</h1>
          <Link to="/budgets" className="b2b-btn b2b-btn--primary">Ver presupuestos</Link>
        </div>
      </div>
    );
  }

  const client = clients.find(c => c.id === budget.clientId);

  const statusConfig: Record<string, { label: string; pill: string }> = {
    draft: { label: 'Borrador', pill: '' },
    sent: { label: 'Enviado', pill: 'b2b-pill--warn' },
    accepted: { label: 'Aceptado', pill: 'b2b-pill--ok' },
    rejected: { label: 'Rechazado', pill: 'b2b-pill--danger' },
    expired: { label: 'Expirado', pill: '' },
  };
  const status = statusConfig[budget.status] || statusConfig.draft;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-1">
          <span className="b2b-eyebrow">Presupuesto</span>
          <h1 className="text-3xl font-extrabold tracking-tight">{budget.number}</h1>
          <p className="b2b-muted">
            {client?.name || 'Sin cliente'}
            {budget.issueDate ? ` · ${formatDate(budget.issueDate)}` : ''}
          </p>
        </div>
        <span className={cn('b2b-pill', status.pill)}>{status.label}</span>
      </div>

      <div className="b2b-bento">
        <div className="b2b-card b2b-col-3">
          <span className="b2b-card__label">Subtotal</span>
          <span className="b2b-card__value">{formatCurrency(budget.subtotal)}</span>
        </div>
        <div className="b2b-card b2b-col-3">
          <span className="b2b-card__label">IVA ({budget.vatRate}%)</span>
          <span className="b2b-card__value">+{formatCurrency(budget.vatAmount)}</span>
        </div>
        <div className="b2b-card b2b-card--accent b2b-col-3">
          <div className="b2b-card__head">
            <span className="b2b-icon"><Send className="w-5 h-5" /></span>
            <span className="b2b-card__label">Total</span>
          </div>
          <span className="b2b-card__value">{formatCurrency(budget.total)}</span>
        </div>
        <div className="b2b-card b2b-col-3">
          <span className="b2b-card__label">Cliente</span>
          <span className="text-lg font-semibold">{client?.name || '—'}</span>
        </div>
      </div>

      <div className="b2b-panel">
        <div className="b2b-panel__head">
          <h2 className="text-lg font-semibold">Desglose</h2>
          <span className="text-sm b2b-muted">{budget.items?.length || 0} partidas</span>
        </div>
        {budget.items && budget.items.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider b2b-muted">
                  <th className="px-6 py-3 font-semibold">Descripción</th>
                  <th className="px-6 py-3 font-semibold text-center">Cant.</th>
                  <th className="px-6 py-3 font-semibold text-right">P. Unit.</th>
                  <th className="px-6 py-3 font-semibold text-right">Importe</th>
                </tr>
              </thead>
              <tbody>
                {budget.items.map(item => (
                  <tr key={item.id} className="border-t border-[var(--b2b-border)] hover:bg-[var(--b2b-surface-muted)] transition-colors">
                    <td className="px-6 py-3">{item.description}</td>
                    <td className="px-6 py-3 text-center b2b-muted">{item.quantity}</td>
                    <td className="px-6 py-3 text-right b2b-muted">{formatCurrency(item.unitPrice)}</td>
                    <td className="px-6 py-3 text-right font-medium">{formatCurrency(item.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-center b2b-muted py-8">Sin partidas</p>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <Link to={`/budgets/${budget.id}/edit`} className="b2b-btn b2b-btn--primary">
          <Edit className="w-4 h-4" /> Editar
        </Link>
        <Link to={`/budgets/${budget.id}/view`} className="b2b-btn b2b-btn--ghost">
          <Printer className="w-4 h-4" /> Imprimir
        </Link>
        <Link to={`/budgets/${budget.id}/view?print=1`} target="_blank" rel="noopener" className="b2b-btn b2b-btn--ghost">
          <Download className="w-4 h-4" /> Descargar PDF
        </Link>
        <button className="b2b-btn b2b-btn--ghost" onClick={() => setShareOpen(true)}>
          <Share2 className="w-4 h-4" /> Compartir
        </button>
      </div>

      <ShareSheet
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        title={`Presupuesto ${budget.number} · ${company?.name || 'Mi empresa'}`}
        text={buildBudgetShareText({
          number: budget.number,
          companyName: company?.name || 'Mi empresa',
          clientName: client?.name,
          total: budget.total,
        })}
        url={absoluteAppUrl(`/budgets/${budget.id}`)}
      />
    </div>
  );
}
